/** Mirrors API `isCouponItemEligible` / `coupon-item-eligibility.util.ts` for client-side discount allocation. */

import type { CouponItemEligibilityProperties } from '@/types';

export type { CouponItemEligibilityProperties };

export interface CouponEligibilityItem {
  sku?: string;
  order_quantity?: number;
  original_unit_price?: number;
  category_ids?: number[];
}

export const couponItemEligibilityHasRestrictions = (
  properties?: CouponItemEligibilityProperties,
): boolean =>
  !!(
    properties?.allowed_skus?.length ||
    properties?.allowed_category_ids?.length ||
    properties?.minimum_allowed_amount != null
  );

const normalizeSku = (sku: string | undefined): string | undefined => {
  if (sku == null || String(sku).trim() === '') return undefined;
  return String(sku).trim();
};

const hasAllowedSku = (
  itemSku: string | undefined,
  allowedSkus?: string[],
): boolean => {
  if (!allowedSkus?.length) return true;
  const normalized = normalizeSku(itemSku);
  if (!normalized) return false;
  return allowedSkus.some((s) => normalizeSku(s) === normalized);
};

const hasAllowedCategory = (
  itemCategoryIds: number[],
  allowedCategoryIds?: number[],
): boolean => {
  if (!allowedCategoryIds?.length) return true;
  return itemCategoryIds.some((categoryId) =>
    allowedCategoryIds.includes(categoryId),
  );
};

const hasAllowedAmount = (
  amount: number,
  minimumAllowedAmount?: number,
): boolean => {
  if (minimumAllowedAmount == null) return true;
  return amount >= minimumAllowedAmount;
};

export const isCouponItemEligible = (
  item: CouponEligibilityItem,
  properties?: CouponItemEligibilityProperties,
): boolean => {
  if (!couponItemEligibilityHasRestrictions(properties)) return true;

  const amount =
    Number(item.order_quantity || 0) * Number(item.original_unit_price || 0);
  const categoryIds = item.category_ids ?? [];
  const allowedSkus = properties?.allowed_skus;
  const allowedCategoryIds = properties?.allowed_category_ids;
  const skuRestricted = !!allowedSkus?.length;
  const categoryRestricted = !!allowedCategoryIds?.length;

  let matchesProductScope = true;
  if (skuRestricted && categoryRestricted) {
    matchesProductScope =
      hasAllowedSku(item.sku, allowedSkus) ||
      hasAllowedCategory(categoryIds, allowedCategoryIds);
  } else if (skuRestricted) {
    matchesProductScope = hasAllowedSku(item.sku, allowedSkus);
  } else if (categoryRestricted) {
    matchesProductScope = hasAllowedCategory(categoryIds, allowedCategoryIds);
  }

  return (
    matchesProductScope &&
    hasAllowedAmount(amount, properties?.minimum_allowed_amount)
  );
};
